/*
 * Product page conversion blocks.
 *
 * Drives four things that all depend on the same two inputs -- which variant is
 * selected and how many of it -- so they share one watcher rather than three:
 *
 *   1. the delivery estimate and its cut-off countdown
 *   2. the free-shipping progress bar
 *   3. the sticky add-to-cart bar
 *
 * Every date here is derived from lead times Ahmad actually publishes on the
 * page (3-5 business days to print, plus transit), not invented. Nothing counts
 * down to a discount, because there are no discounts. If you are tempted to add
 * a fake stock number or a timer that resets on reload, read UPDATE-NUMBERS.md
 * first -- unsupported storefront claims are this store's recurring bug.
 *
 * Timezone handling: the shop's wall clock is derived from the shopper's own
 * Date.now(), converted into the store's IANA zone with Intl. Every date is a
 * Date whose *UTC* fields read as the shop's wall clock, so every getUTC* and
 * every Intl format with timeZone: 'UTC' below is deliberate. Liquid's
 * render-time epoch and %z offset are only the fallback for a browser with no
 * Intl timezone support -- a cached page must not run the countdown late.
 */
(function () {
  function parseOffsetSeconds(raw) {
    // '%z' renders as +0530 / -0400
    const match = /^([+-])(\d{2})(\d{2})$/.exec((raw || '').trim());
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (parseInt(match[2], 10) * 3600 + parseInt(match[3], 10) * 60);
  }

  function isoDate(date) {
    // The Date carries the shop's wall clock in its UTC fields, so this is the
    // shop's calendar day, not the viewer's.
    return date.toISOString().slice(0, 10);
  }

  function isBusinessDay(date, holidays) {
    const day = date.getUTCDay();
    if (day === 0 || day === 6) return false;
    return !(holidays && holidays.has(isoDate(date)));
  }

  function nextBusinessDay(date, holidays) {
    const next = new Date(date.getTime());
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (!isBusinessDay(next, holidays));
    return next;
  }

  function addBusinessDays(date, days, holidays) {
    let out = new Date(date.getTime());
    for (let i = 0; i < days; i += 1) out = nextBusinessDay(out, holidays);
    return out;
  }

  function previousBusinessDay(date, holidays) {
    const previous = new Date(date.getTime());
    do {
      previous.setUTCDate(previous.getUTCDate() - 1);
    } while (!isBusinessDay(previous, holidays));
    return previous;
  }

  // The holiday order-by date is the same walk as the delivery window, run
  // backwards from the day the parcel has to be in someone's hands. Same
  // weekends, same USPS holiday list, so the two can never disagree.
  function subtractBusinessDays(date, days, holidays) {
    let out = new Date(date.getTime());
    for (let i = 0; i < days; i += 1) out = previousBusinessDay(out, holidays);
    return out;
  }

  function parseISODate(raw) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((raw || '').trim());
    if (!match) return null;
    const stamp = Date.UTC(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    return isNaN(stamp) ? null : new Date(stamp);
  }

  function startOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  function formatMoney(cents, currency, locale) {
    // The shopper's presentment currency, formatted by the browser. Falls back
    // to Shopify.formatMoney where a theme app defines it, then to a plain
    // dollar figure -- never to '$' in front of a non-dollar amount when a
    // currency code is known.
    const amount = cents / 100;
    if (currency) {
      try {
        return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency }).format(amount);
      } catch (error) {
        /* unknown code; fall through */
      }
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (error) {
        /* fall through to the plain rendering below */
      }
    }
    return '$' + amount.toFixed(2);
  }

  class ProductConversion {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.locale = document.documentElement.lang || 'en';

      this.variants = this.readJSON('.cpdp-variants');
      this.delivery = root.querySelector('.cpdp-delivery');
      this.ship = root.querySelector('.cpdp-ship');
      this.sticky = document.querySelector('.cpdp-sticky[data-section="' + this.sectionId + '"]');

      this.priceNode = root.querySelector('#price-' + this.sectionId);
      this.variantInput = root.querySelector('input[name="id"].product-variant-id');
      this.quantityInput = root.querySelector('.quantity__input');

      this.cartTotal = parseInt((this.ship && this.ship.dataset.cartTotal) || '0', 10) || 0;
      this.cartItems = parseInt(
        (this.ship && this.ship.dataset.cartItems) || (this.delivery && this.delivery.dataset.cartItems) || '0',
        10
      ) || 0;

      this.tick = this.tick.bind(this);
      this.update = this.update.bind(this);

      this.setupDelivery();
      this.setupSticky();
      this.watch();
      this.update();
      this.refreshCart();
      this.setupScaleFigureCta();
    }

    readJSON(selector) {
      const node = this.root.querySelector(selector);
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /* ---------------- what is selected right now ---------------- */

    currentVariant() {
      const id = this.variantInput && parseInt(this.variantInput.value, 10);
      if (!id) return this.variants[0] || null;
      return this.variants.find((variant) => variant.id === id) || this.variants[0] || null;
    }

    currentQuantity() {
      const value = this.quantityInput && parseInt(this.quantityInput.value, 10);
      return value > 0 ? value : 1;
    }

    watch() {
      // Dawn swaps the innerHTML of #price-<section> on every variant change and
      // sets the hidden id input as a property, which no attribute observer would
      // see. The price node is the reliable signal.
      if (this.priceNode && 'MutationObserver' in window) {
        new MutationObserver(this.update).observe(this.priceNode, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
      this.root.addEventListener('change', this.update);
      this.root.addEventListener('input', (event) => {
        if (event.target.classList.contains('quantity__input')) this.update();
      });
      window.addEventListener('pageshow', () => this.refreshCart());
    }

    refreshCart() {
      if (!this.ship) return;
      // A cached page can carry a stale cart total, and the shopper may have
      // added something in another tab.
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart || typeof cart.total_price !== 'number') return;
          this.cartTotal = cart.total_price;
          if (typeof cart.item_count === 'number') this.cartItems = cart.item_count;
          this.update();
        })
        .catch(() => {
          /* the Liquid-rendered total stands */
        });
    }

    update() {
      // Dawn replaces the whole <variant-selects> element on every variant
      // change, so the pill prices have to be written again each time. This
      // runs first because the price node is what signals the swap is done.
      this.decorateSizePills();
      const variant = this.currentVariant();
      if (!variant) return;
      const quantity = this.currentQuantity();
      this.updateShipping(variant, quantity);
      this.updateDelivery(variant, quantity);
      this.updateSticky(variant);
    }

    /* ---------------- price on each size pill (F8) ---------------- */

    // A 5in T-Rex is $28.74 and a 13in is $119.99 -- a 4.2x spread that could
    // only be discovered by clicking. The figures come from the variant data
    // already on the page, formatted by Shopify's own money filter, so nothing
    // here is a second source of truth for a price.
    decorateSizePills() {
      const picker = this.root.querySelector('variant-selects');
      if (!picker) return;
      if (picker.dataset.cpdpSizePrices !== 'true') return;
      if (!this.variants.length) return;

      const names = (picker.dataset.cpdpSizeNames || 'Size')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      if (!names.length) return;

      picker.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
        const optionName = (input.dataset.optionName || '').trim().toLowerCase();
        if (names.indexOf(optionName) === -1) return;

        const index = parseInt(input.dataset.optionPosition, 10) - 1;
        if (!(index >= 0)) return;

        const label = this.labelFor(input);
        if (!label || label.querySelector('.cpdp-pill__price')) return;

        // The cheapest variant at this size, as the block's setting text says.
        // Today every colour costs the same at a given size (checked on the
        // T-Rex, 17 Sep 2026), so this is simply that size's price -- but if a
        // colour is ever priced up, the pill still never shows more than the
        // size can be had for.
        const value = input.value;
        let match = null;
        this.variants.forEach((variant) => {
          if (!Array.isArray(variant.options) || String(variant.options[index]) !== value) return;
          if (!match || variant.price < match.price) match = variant;
        });
        if (!match) return;

        const price =
          match.price_formatted || formatMoney(match.price, picker.dataset.currencyCode, this.locale);
        if (!price) return;

        const tag = document.createElement('span');
        tag.className = 'cpdp-pill__price';
        tag.textContent = price;
        label.appendChild(tag);
      });
    }

    labelFor(input) {
      const sibling = input.nextElementSibling;
      if (sibling && sibling.tagName === 'LABEL') return sibling;
      const id = input.getAttribute('id');
      if (!id) return null;
      // Scoped to this input's own fieldset on purpose: Dawn leaves the previous
      // <variant-selects> in the DOM for half a second after a variant change,
      // and its labels still point at the ids they had before the swap.
      const scope = input.closest('fieldset') || input.parentNode;
      return Array.prototype.find.call(
        scope.querySelectorAll('label[for]'),
        (node) => node.getAttribute('for') === id
      );
    }

    /* ---------------- the scale figure's "choose this size" ---------------- */

    // It used to be a ?variant= link that reloaded the page, which threw away
    // everything the shopper had already scrolled past. Selecting the radio and
    // scrolling up does the same job without a round trip. The href stays as it
    // was, so the button still works with no JavaScript at all.
    setupScaleFigureCta() {
      if (!this.root.querySelector('variant-selects')) return;

      this.ctaHandler = (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        // The opt-in attribute is written by custom-scale-figure.liquid, so the
        // old reload behaviour is one checkbox away in the theme editor.
        const cta = target.closest('a[data-csf-cta][data-csf-select-size]');
        if (!cta) return;

        // The figure has already put the exact variant in the href. Reading the
        // size out of that variant beats matching label text, and it keeps the
        // shopper's chosen colour: only the size radio is touched.
        const href = cta.getAttribute('href') || '';
        const found = /[?&]variant=(\d+)/.exec(href);
        if (!found) return;

        const variant = this.variants.find((row) => String(row.id) === found[1]);
        if (!variant || !Array.isArray(variant.options)) return;

        const picker = this.root.querySelector('variant-selects');
        if (!picker) return;

        const names = (picker.dataset.cpdpSizeNames || 'Size')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        const radio = Array.prototype.find.call(
          picker.querySelectorAll('input[type="radio"][data-option-position]'),
          (input) => {
            const optionName = (input.dataset.optionName || '').trim().toLowerCase();
            if (names.length && names.indexOf(optionName) === -1) return false;
            const index = parseInt(input.dataset.optionPosition, 10) - 1;
            return index >= 0 && String(variant.options[index]) === input.value;
          }
        );
        // No matching radio (dropdown picker, renamed option): leave the link
        // alone and let the page navigate the way it always did.
        if (!radio) return;

        event.preventDefault();
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const buyBox =
          this.root.querySelector('.product-form__buttons') ||
          this.root.querySelector('.product__info-container') ||
          this.root;
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        buyBox.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      };

      document.addEventListener('click', this.ctaHandler);
    }

    /* ---------------- free shipping progress ---------------- */

    updateShipping(variant, quantity) {
      const node = this.ship;
      if (!node) return;

      const threshold = parseInt(node.dataset.threshold, 10) || 0;
      if (!threshold) return;

      const subtotal = this.cartTotal + variant.price * quantity;
      const remaining = Math.max(0, threshold - subtotal);
      const met = remaining === 0;
      const currency = node.dataset.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';

      node.classList.toggle('cpdp-ship--met', met);

      const message = node.querySelector('.cpdp-ship__message');
      if (message) {
        message.innerHTML = met
          ? node.dataset.metText
          : node.dataset.awayText.replace('[amount]', '<strong>' + formatMoney(remaining, currency, this.locale) + '</strong>');
      }

      const fill = node.querySelector('.cpdp-ship__fill');
      if (fill) {
        const percent = Math.min(100, Math.round((subtotal / threshold) * 100));
        fill.style.width = percent + '%';
        const track = node.querySelector('.cpdp-ship__track');
        if (track) track.setAttribute('aria-valuenow', String(percent));
      }
    }

    /* ---------------- delivery estimate ---------------- */

    setupDelivery() {
      const node = this.delivery;
      if (!node) return;

      const offsetSeconds = parseOffsetSeconds(node.dataset.tzOffset);
      const nowEpoch = parseInt(node.dataset.now, 10) || Math.floor(Date.now() / 1000);

      // Fallback clock only: the render-time epoch shifted into the shop's wall
      // clock. shopClock() prefers the live Intl conversion below.
      this.shopNow = new Date((nowEpoch + offsetSeconds) * 1000);
      this.renderedAt = Date.now();
      this.timeZone = node.dataset.timezone || '';
      this.zoneParts = null;
      if (this.timeZone) {
        try {
          this.zoneParts = new Intl.DateTimeFormat('en-US', {
            timeZone: this.timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
        } catch (error) {
          this.zoneParts = null; // unknown zone name or no Intl support
        }
      }

      this.cutoffHour = parseInt(node.dataset.cutoffHour, 10);
      this.cutoffMinute = parseInt(node.dataset.cutoffMinute, 10) || 0;
      this.printMin = parseInt(node.dataset.printMin, 10) || 0;
      this.printMax = parseInt(node.dataset.printMax, 10) || 0;
      this.transitMin = parseInt(node.dataset.transitMin, 10) || 0;
      this.transitMax = parseInt(node.dataset.transitMax, 10) || 0;
      this.slowExtra = parseInt(node.dataset.slowExtra, 10) || 0;
      this.multiExtra = parseInt(node.dataset.multiExtra, 10) || 0;
      this.slowOptions = (node.dataset.slowOptions || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      // Products with no size option to match (the Big 1:3 T-Rex) are matched
      // on their tags instead.
      this.slowTags = (node.dataset.slowTags || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      this.productTags = (node.dataset.productTags || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);

      // Days nothing gets printed or carried. Without these the estimate walks
      // straight through Labor Day and promises a date nobody can hit.
      this.holidays = new Set(
        (node.dataset.holidays || '')
          .split(',')
          .map((value) => value.trim())
          .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
      );

      // F21: one more transit leg (USPS Priority Express, 1-2 days) and the
      // holiday order-by target. Both are fed through the same walk as the
      // window above, so there is exactly one date engine on this page.
      this.expressDays = parseInt(node.dataset.expressDays, 10) || 0;
      this.holidayTarget = parseISODate(node.dataset.holidayTarget);

      this.dateFormat = new Intl.DateTimeFormat(this.locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
      this.dayFormat = new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: 'UTC' });

      node.hidden = false;
      this.tick();
    }

    shopClock() {
      // A Date whose UTC fields read as the shop's wall clock right now, taken
      // from the device clock and the store's zone rules -- so a page served
      // from cache, or left open across a DST change, still counts correctly.
      if (this.zoneParts) {
        try {
          const parts = this.zoneParts.formatToParts(new Date());
          const read = (type) => parseInt((parts.find((part) => part.type === type) || {}).value, 10);
          let hour = read('hour');
          if (hour === 24) hour = 0;
          const stamp = Date.UTC(read('year'), read('month') - 1, read('day'), hour, read('minute'), read('second'));
          if (!isNaN(stamp)) return new Date(stamp);
        } catch (error) {
          /* fall back to the render-time clock */
        }
      }
      return new Date(this.shopNow.getTime() + (Date.now() - this.renderedAt));
    }

    cutoffAt(day) {
      if (isNaN(this.cutoffHour)) return null;
      return new Date(startOfDay(day).getTime() + this.cutoffHour * 3600000 + this.cutoffMinute * 60000);
    }

    isSlow(variant, quantity) {
      let extra = 0;
      let hit = false;
      if (this.slowOptions.length && Array.isArray(variant.options)) {
        hit = variant.options.some((option) =>
          this.slowOptions.indexOf(String(option).trim().toLowerCase()) !== -1
        );
      }
      if (!hit && this.slowTags.length && this.productTags.length) {
        hit = this.productTags.some((tag) => this.slowTags.indexOf(tag) !== -1);
      }
      if (hit) extra += this.slowExtra;
      // "Multi-item orders take longer" means the whole order, so count what is
      // already in the cart as well as this product's quantity.
      if (quantity + (this.cartItems || 0) > 1) extra += this.multiExtra;
      return extra;
    }

    updateDelivery() {
      // Recomputed on every tick anyway; this exists so update() can call it.
      this.tick();
    }

    tick() {
      const node = this.delivery;
      if (!node) return;

      const now = this.shopClock();
      const today = startOfDay(now);
      const cutoff = this.cutoffAt(today);
      const beforeCutoff = cutoff ? now.getTime() < cutoff.getTime() : true;
      const startsToday = isBusinessDay(now, this.holidays) && beforeCutoff;
      const start = startsToday ? today : nextBusinessDay(today, this.holidays);

      const variant = this.currentVariant();
      const extra = variant ? this.isSlow(variant, this.currentQuantity()) : 0;

      const earliest = addBusinessDays(start, this.printMin + extra + this.transitMin, this.holidays);
      const latest = addBusinessDays(start, this.printMax + extra + this.transitMax, this.holidays);

      const dates = node.querySelector('.cpdp-delivery__dates');
      if (dates) {
        dates.textContent =
          earliest.getTime() === latest.getTime()
            ? this.dateFormat.format(earliest)
            : this.dateFormat.format(earliest) + ' – ' + this.dateFormat.format(latest);
      }

      // F21. "Ships by" is the slowest print date from the same start day --
      // the date the parcel leaves here, not the date it arrives, so it is the
      // one promise that is entirely inside Ahmad's control. Express is that
      // date plus USPS Priority Express transit.
      const shipsBy = addBusinessDays(start, this.printMax + extra, this.holidays);
      this.fillDateLine(
        node.querySelector('.cpdp-delivery__ships'),
        node.dataset.shipsByText,
        shipsBy
      );
      if (this.expressDays > 0) {
        this.fillDateLine(
          node.querySelector('.cpdp-delivery__express'),
          node.dataset.expressText,
          addBusinessDays(shipsBy, this.expressDays, this.holidays)
        );
      }

      // The holiday order-by date: walk the same print + transit days backwards
      // from the day it has to be there. The row hides itself once an order
      // placed now would no longer start on or before that date -- so on the
      // order-by day itself it disappears at the 6 PM cut-off, not at
      // midnight, rather than printing a deadline nobody can still meet.
      const holidayRow = node.querySelector('.cpdp-delivery__row--holiday');
      if (holidayRow) {
        const copyNode = holidayRow.querySelector('.cpdp-delivery__holiday');
        const template = node.dataset.holidayText || '';
        if (this.holidayTarget && copyNode && template) {
          // Anchor on the last business day on or before the target. The walk
          // forward only ever lands on business days, so counting back from a
          // holiday or weekend target (25 Dec is both a USPS holiday and the
          // default) would spend one step reaching the 24th and promise a
          // date one business day too late -- "order by 11 Dec" for a parcel
          // that would land on 28 Dec.
          let lastArrival = this.holidayTarget;
          if (!isBusinessDay(lastArrival, this.holidays)) {
            lastArrival = previousBusinessDay(lastArrival, this.holidays);
          }
          const orderBy = subtractBusinessDays(
            lastArrival,
            this.printMax + extra + this.transitMax,
            this.holidays
          );
          if (orderBy.getTime() >= start.getTime()) {
            copyNode.innerHTML = template.replace('[date]', this.dateFormat.format(orderBy));
            holidayRow.hidden = false;
          } else {
            holidayRow.hidden = true;
          }
        } else {
          holidayRow.hidden = true;
        }
      }

      // Re-arm before the early return below, so the window still rolls over
      // at the cut-off and at midnight when the countdown row is switched off.
      clearTimeout(this.timer);
      this.timer = setTimeout(this.tick, 1000);

      const cutoffRow = node.querySelector('.cpdp-delivery__row--cutoff');
      if (!cutoffRow) return;

      const clock = cutoffRow.querySelector('.cpdp-delivery__clock');
      const copy = cutoffRow.querySelector('.cpdp-delivery__text');

      if (startsToday && cutoff) {
        const remaining = cutoff.getTime() - now.getTime();
        if (clock) clock.textContent = this.formatRemaining(remaining);
        if (copy) copy.innerHTML = node.dataset.todayText;
        cutoffRow.hidden = false;
      } else {
        // Past the cut-off, or the weekend: say which day it actually joins
        // rather than counting down to something that already passed.
        if (clock) clock.textContent = '';
        if (copy) copy.innerHTML = node.dataset.nextText.replace('[day]', this.dayFormat.format(start));
        cutoffRow.hidden = false;
      }
    }

    // Empty template or no node means the setting is off: leave the line hidden
    // rather than printing a bare date with nothing to say about it.
    fillDateLine(node, template, date) {
      if (!node) return;
      if (!template || !date) {
        node.hidden = true;
        return;
      }
      node.innerHTML = template.replace('[date]', this.dateFormat.format(date));
      node.hidden = false;
    }

    formatRemaining(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      if (hours > 0) return hours + 'h ' + minutes + 'm';
      if (minutes > 0) return minutes + 'm ' + seconds + 's';
      return seconds + 's';
    }

    /* ---------------- sticky add to cart ---------------- */

    setupSticky() {
      const bar = this.sticky;
      if (!bar) return;

      const anchor = this.root.querySelector('#ProductSubmitButton-' + this.sectionId);
      const button = bar.querySelector('.cpdp-sticky__button');

      if (button && anchor) {
        button.addEventListener('click', () => {
          // Go through the real button so product-form.js owns the request --
          // including the native-post fallback that carries a file upload.
          anchor.click();
        });
      }

      if (!anchor) return;

      this.stickyAnchor = anchor;

      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(() => this.updateStickyVisibility(), {
          rootMargin: '0px 0px -40px 0px',
        });
        this.stickyObserver.observe(anchor);
      }
    }

    // Kept as its own method rather than living inside the observer callback so
    // the rule can be read -- and tested -- without having to provoke a scroll.
    updateStickyVisibility() {
      const bar = this.sticky;
      const anchor = this.stickyAnchor;
      if (!bar || !anchor) return false;

      // Only once the real buy button has gone off the top. Sitting below it,
      // still on the way down the page, the bar would cover content for no
      // reason -- the button the shopper wants is already on screen.
      const rect = anchor.getBoundingClientRect();
      const visible = rect.bottom < 0;

      bar.classList.toggle('cpdp-sticky--visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      return visible;
    }

    updateSticky(variant) {
      const bar = this.sticky;
      if (!bar) return;

      const price = bar.querySelector('.cpdp-sticky__price');
      if (price) {
        price.textContent = variant.price_formatted
          ? variant.price_formatted
          : formatMoney(variant.price, bar.dataset.currency, this.locale);
      }

      const option = bar.querySelector('.cpdp-sticky__option');
      if (option) option.textContent = variant.title || '';

      const button = bar.querySelector('.cpdp-sticky__button');
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? bar.dataset.addText : bar.dataset.soldText;
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('product-info[data-cpdp]').forEach((root) => {
      if (root.cpdp) return;
      root.cpdp = new ProductConversion(root);
    });
  }

  function destroy(scope) {
    (scope || document).querySelectorAll('product-info[data-cpdp]').forEach((root) => {
      const instance = root.cpdp;
      if (!instance) return;
      clearTimeout(instance.timer);
      if (instance.stickyObserver) instance.stickyObserver.disconnect();
      // Delegated at document level, so it outlives the section unless it is
      // taken off by hand -- the theme editor reloads this section constantly.
      if (instance.ctaHandler) document.removeEventListener('click', instance.ctaHandler);
      root.cpdp = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // The theme editor re-renders the section on every setting change; without
  // these the delivery block stays hidden and the old tick loop runs on a
  // detached node.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
